"use client"

import { useEffect, useState } from "react"
import { motion } from "framer-motion"
import { useNavigate } from "react-router-dom"
import image from "../../assets/login.jpg"
import { LuEyeClosed, LuEye } from "react-icons/lu"
import { childVariants, containerVariants } from "../../components/auth/signup/motion"
import Navbar from "../../components/auth/common/Navbar"

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL
const EMAIL_MAX_LENGTH = 254
const PASSWORD_MAX_LENGTH = 25

export default function LoginForm() {
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  })
  const [error, setError] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  useEffect(() => {
    const userData = localStorage.getItem("user");
    const parsedUser = JSON.parse(userData);
    // console.log(parsedUser)
    if (parsedUser) {
        if (parsedUser.role === "admin") navigate("/admin")
        if (parsedUser.role === "client") navigate("/client")
        if (parsedUser.role === "agent") navigate("/agent")
        if (parsedUser.role === "sub-admin") navigate("/agent")
    }
}, [])

  const handleLogin = async (e) => {
    e.preventDefault()
    setError("")
    setIsLoading(true)

    try {
      const response = await fetch(`${API_BASE_URL}/api/user/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      })

      const data = await response.json()
      if (!response.ok) {
        throw new Error(data.message)
      }
      localStorage.setItem("token", data.body.token)
      localStorage.setItem("user", JSON.stringify(data.body.user))
      if (data?.body?.role === "admin") {
        navigate("/admin")
      }
      if (data?.body?.role === "client") {
        navigate("/client")
      }
      if (data?.body?.role === "agent") {
        navigate("/agent")
      }
      if (data?.body?.role === "sub-admin") {
        navigate("/agent")
      }
    } catch (error) {
      setError(error.message)
    } finally {
      setIsLoading(false)
    }
  }

  const [isSmallScreen, setIsSmallScreen] = useState(false)

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 1020)
    }

    handleResize() // Check on mount
    window.addEventListener("resize", handleResize)

    return () => window.removeEventListener("resize", handleResize)
  }, [])

  return (
    <div
      className={`min-h-screen flex items-center justify-center select-none ${isSmallScreen ? "bg-cover bg-center" : ""}`}
      style={{ backgroundImage: isSmallScreen ? `url(${image})` : "none" }}
    >
      <Navbar />
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="w-[90%] h-[80vh] lg:h-max lg:w-max bg-white/80 flex flex-row justify-center items-center border border-[#CBD5E1] rounded-[10px] shadow-custom overflow-hidden mt-12"
      >
        {/* Image Section */}
        <motion.div
          variants={childVariants}
          className="hidden lg:block w-[445px] 2xl:w-[630px] h-[552px] overflow-hidden"
        >
          <div className="relative w-full h-full">
            <img loading={"lazy"} src={image || "/placeholder.svg"} alt="Travel background" className="w-full h-full object-cover" />
          </div>
        </motion.div>

        <motion.div
          variants={childVariants}
          className="flex flex-col w-[300px] md:w-[400px] 2xl:w-[500px] px-8 py-5 relative"
        >
          <motion.div variants={childVariants} className="mb-6">
            <h1 className="text-2xl mb-1">Hello!</h1>
            <p className="text-gray-600">Welcome back</p>
          </motion.div>

          <motion.form variants={childVariants} onSubmit={handleLogin} className="space-y-4">
            <motion.div variants={childVariants}>
              <div className="flex justify-start items-center gap-1 mb-1">
                <label className="block text-gray-700 text-sm">Email</label>
                <span className="text-[#DF6E6E] text-lg">*</span>
              </div>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value.slice(0, EMAIL_MAX_LENGTH) })}
                className="w-full px-2.5 py-1.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 border-[#BBBBBB] bg-[#F0F0F0] text-sm"
                maxLength={EMAIL_MAX_LENGTH}
                required
              />
            </motion.div>

            <motion.div variants={childVariants}>
              <div className="flex justify-start items-center gap-1 mb-1">
                <label className="block text-gray-700 text-sm">Password</label>
                <span className="text-[#DF6E6E] text-lg">*</span>
              </div>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value.slice(0, PASSWORD_MAX_LENGTH) })}
                  className="w-full px-2.5 py-1.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 border-[#BBBBBB] bg-[#F0F0F0] text-sm"
                  maxLength={PASSWORD_MAX_LENGTH}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <LuEye className="w-4 h-4" /> : <LuEyeClosed className="w-4 h-4" />}
                </button>
              </div>
            </motion.div>

            <motion.div variants={childVariants} className="text-right">
              <button
                onClick={() => navigate("/forgot-password")}
                type="button"
                className="text-sm text-gray-600 hover:underline"
              >
                Forgot Password?
              </button>
            </motion.div>

            <motion.div variants={childVariants}>
              {isLoading ? (
                <button
                  className="w-full px-4 py-2 text-white bg-[#025177] rounded-full h-[40px] flex items-center justify-center"
                  disabled
                >
                  <span className="loader"></span>
                </button>
              ) : (
                <button
                  type="submit"
                  className="w-full py-2 text-white bg-[#036494] rounded-full hover:bg-[#025177] transition-colors font-light"
                >
                  Sign In
                </button>
              )}
            </motion.div>

            {/* Error Message */}
            {error && (
              <motion.p variants={childVariants} className="text-sm text-red-500 text-center">
                {error}
              </motion.p>
            )}

            <motion.div variants={childVariants} className="text-center text-sm text-gray-600">
              Don't have an account?{" "}
              <button onClick={() => navigate("/signup")} type="button" className="text-[#036494] hover:underline">
                Sign Up
              </button>
            </motion.div>
          </motion.form>
        </motion.div>
      </motion.div>
    </div>
  )
}